import type { ValidationIssue } from '@verifyloop/core';

/** Group issues by the JSON path the server named, so each lands on its field. */
export function indexIssues(
  issues: readonly ValidationIssue[],
): Map<string, ValidationIssue[]> {
  const map = new Map<string, ValidationIssue[]>();
  for (const issue of issues) {
    const list = map.get(issue.path) ?? [];
    list.push(issue);
    map.set(issue.path, list);
  }
  return map;
}

/**
 * Issues that apply to a step, whether they name the step itself
 * (`$.planSteps[2]`) or one of its fields (`$.planSteps[2].description`).
 */
export function stepIssues(map: Map<string, ValidationIssue[]>, index: number): ValidationIssue[] {
  const prefix = `$.planSteps[${index}]`;
  const collected: ValidationIssue[] = [];
  for (const [path, list] of map) {
    if (path === prefix || path.startsWith(`${prefix}.`) || path.startsWith(`${prefix}[`)) {
      collected.push(...list);
    }
  }
  return collected;
}

export function fieldIssues(
  map: Map<string, ValidationIssue[]>,
  path: string,
): ValidationIssue[] {
  return map.get(path) ?? [];
}

export function worstSeverity(issues: readonly ValidationIssue[]): 'error' | 'warning' | null {
  if (issues.some((issue) => issue.severity === 'error')) return 'error';
  if (issues.length > 0) return 'warning';
  return null;
}

export function IssueList({
  issues,
  dense = false,
}: {
  issues: readonly ValidationIssue[];
  dense?: boolean;
}): JSX.Element | null {
  if (issues.length === 0) return null;
  return (
    <ul className={`issue-list${dense ? ' issue-list--dense' : ''}`}>
      {issues.map((issue, index) => (
        <li key={`${issue.path}:${issue.message}:${index}`} className={`issue issue--${issue.severity}`}>
          {issue.severity === 'warning' ? <span className="issue__badge">warning</span> : null}
          <code className="issue__path">{issue.path}</code>
          <span className="issue__message">{issue.message}</span>
        </li>
      ))}
    </ul>
  );
}